<template>
  <div class="hotel-card">
    <div class="hotel-card__top">
      <button v-if="clearable" @click="onCardDelete" type="button" class="hotel-card__delete">
        <img src="@/assets/img/clear-small-gray-icon.svg" alt="">
      </button>
      <div class="hotel-card__count">{{ order_number }}.</div>
      <div class="hotel-card__accomodation_type">
        <app-room-icon :accomodation_type="accomodation_type" :disabled="true" />
      </div>
      <div class="hotel-card__info">
        <div class="hotel-card__name">{{ name }} {{ short_name ? `(${short_name})` : '' }}</div>
        <div class="hotel-card__times">
          {{ calcNumberOfNights }} Nights | {{ formatDate(check_in) }} - {{ formatDate(check_out) }}&nbsp;<template v-if="mp">| {{ mp }}</template>
        </div>
        <div v-if="early || late" class="hotel-card__early-late">
          <div v-if="early" class="hotel-card__early">
            <img src="@/assets/img/clock-blue.svg" alt="">
            <span>Early Check-in: {{ early }}</span>
          </div>
          <div v-if="late" class="hotel-card__early">
            <img src="@/assets/img/clock-blue.svg" alt="">
            <span>Late Check-out: {{ late }}</span>
          </div>
        </div>
      </div>
      <app-input
        v-if="count"
        @input="onCountInput"
        v-model.number="count_local"
        type="number"
        min="1"
        class="input--45 input--center"
        :readonly="!clearable || bedInTwin"
      />
    </div>
    <div v-if="bedInTwin && property" class="hotel-card__radios">
      <app-form-field label="Choose the action">
        <app-cells :indent="false">
          <app-radio
            v-model="bed_in_twin_action"
            value="create"
            :name="`${order_number}_${bed_in_twin_action}`"
          >
            <template #label>Create</template>
          </app-radio>
          <app-radio
            v-model="bed_in_twin_action"
            value="link"
            :name="`${order_number}_${bed_in_twin_action}`"
          >
            <template #label>Link</template>
          </app-radio>
          <app-select
            v-if="bed_in_twin_action === 'link'"
            @input="onBITActionChange"
            v-model="bed_in_twin_room_id"
            :options="rooms_options"
            :clearable="false"
            :searchable="false"
            :display-field="option => `${option.reservation.group_name} ${option.room ? `(${option.room.short_name})` : '(empty)'}`"
            label="id"
            placeholder="Choose room"
            class="select--grow1"
            :disabled="!rooms_options || rooms_options.length < 1"
          />
        </app-cells>
      </app-form-field>
    </div>
    <div v-if="clients_selected && clients_selected.length && rooming_list" class="hotel-card__middle">
      <div class="hotel-card__clients">
        <div v-for="(client, index) in clients_selected" :key="index" class="hotel-card__client">
          <img v-if="client.age_type" :src="require(`@/assets/img/${client.age_type}-icon.svg`)" alt="">
          <img v-else src="@/assets/img/age-null-icon.svg" alt="">
          <span>{{ client.first_name }} {{ client.last_name || '' }}</span>
        </div>
      </div>
    </div>
    <div v-if="!rooming_list" class="hotel-card__bottom">
      <div class="hotel-room__multiple">
        <app-select
          @input="onChangeSelect"
          v-model="clients"
          :options="clients_options"
          :clearable="false"
          :selectable="onCalcPaxLimit"
          :closeOnSelect="false"
          label="full_name"
          multiple
          placeholder="Selected clients"
        >
          <template #custom-option="option">
            <img v-if="option.age_type" :src="require(`@/assets/img/${option.age_type}-icon.svg`)" alt="" class="select__img">
            <img v-else src="@/assets/img/age-null-icon.svg" alt="">
            {{ option.full_name }}
          </template>
        </app-select>
      </div>
    </div>
  </div>
</template>

<script>
import AppRoomIcon from '@/components/AppRoomIcon.vue'
import { format, parseISO } from 'date-fns'
import { getHotelManagementReservationsRooms } from '@/http'

export default {
  name: 'AppHotelCard',
  components: { AppRoomIcon },
  props: {
    clearable: {
      type: Boolean,
      default: true
    },
    order_number: {
      type: Number,
      default: 1,
    },
    accomodation_type: {
      type: Object,
      default: () => {},
    },
    name: {
      type: String,
      default: '',
    },
    check_in: {
      type: String,
      default: '',
    },
    check_out: {
      type: String,
      default: '',
    },
    mp: {
      type: String,
      default: '',
    },
    clients_selected: {
      type: Array,
      default: () => []
    },
    clients_options: {
      type: Array,
      default: () => []
    },
    rooming_list: {
      type: Boolean,
      default: true
    },
    early: {
      type: String,
      default: '',
    },
    late: {
      type: String,
      default: '',
    },
    count: {
      type: Number,
      default: null,
    },
    bedInTwin: {
      type: Boolean,
      default: false,
    },
    property: {
      type: Object,
      default: () => {},
    },
    short_name: {
      type: String,
      default: '',
    }
  },
  data() {
    return {
      count_local: this.count,
      clients: [],
      bed_in_twin_action: 'create',
      bed_in_twin_room_id: null,
      rooms_options: [],
    }
  },
  watch: {
    count(val) {
      this.count_local = val
    },
    clients_selected: {
      handler(val) {
        this.clients = val
      },
      immediate: true,
    },
    bed_in_twin_action: {
      handler(val) {
        this.$emit('changeSomeField', { value: val, field: 'bed_in_twin_action' })

        if (val === 'link') {
          getHotelManagementReservationsRooms(
            {
              check_in: this.check_in,
              check_out: this.check_out,
              share_vacant: true,
              property: this.property.code
            }
          )
            .then(res => { this.rooms_options = res.data })
        }
        this.$emit('changeSomeField', { value: null, field: 'bed_in_twin_room_id' })
      },
      immediate: true,
    }
  },
  computed: {
    calcNumberOfNights() {
      if (this.check_in && this.check_out) {
        const date1 = new Date(this.check_in)
        const date2 = new Date(this.check_out)
        const timeDiff = date2.getTime() - date1.getTime()
        return Math.ceil(timeDiff / (1000 * 3600 * 24)) < 0 ? 0 : Math.ceil(timeDiff / (1000 * 3600 * 24))
      } else {
        return ''
      }
    },
  },
  methods: {
    onBITActionChange() {
      this.$emit('changeSomeField', { value: this.bed_in_twin_room_id.id, field: 'bed_in_twin_room_id' })
    },
    onCalcPaxLimit() {
      if (this.accomodation_type && this.clients_selected) {
        return this.clients_selected.length < this.accomodation_type.pax
      }
    },
    onChangeSelect() {
      this.$emit('onChangeRoomClients', this.clients)
    },
    onCardDelete() {
      this.$emit('onCardDelete')
    },
    onCountInput() {
      if (typeof this.count_local === 'number') this.$emit('onCountInput', this.count_local)
    },
    formatDate(date) {
      if (!date) return ''
      return format(parseISO(date), 'dd MMM yyyy')
    },
  }
}
</script>

<style scoped lang="sass">
.hotel-card
  position: relative
  margin-bottom: 10px
  border: 1px solid #eaeaea
  border-radius: 10px
  background-color: #fff

.hotel-card__top
  display: flex
  align-items: center
  gap: 12px
  padding: 12px 30px 12px 12px

.hotel-card__bottom
  padding: 12px 25px 12px 12px
  border-top: 1px solid #e7e7e7

.hotel-card__delete
  position: absolute
  top: 2px
  right: 2px
  padding: 10px
  line-height: 0

.hotel-card__count
  display: flex
  align-items: center
  justify-content: center
  width: 36px
  height: 35px
  border-radius: 8px
  background-color: #eee
  color: #181818
  font-size: 12px
  font-weight: 600

.hotel-card__name
  color: #000
  font-size: 14px
  font-weight: 600

.hotel-card__early-late
  display: flex
  align-items: center
  gap: 30px
  margin-top: 5px

.hotel-card__early
  display: flex
  align-items: center
  gap: 6px
  color: #439ad1

.hotel-card__clients
  display: flex
  align-items: center
  gap: 20px
  padding: 12px 25px 12px 12px
  border-top: 1px solid #e7e7e7

.hotel-card__client
  display: flex
  align-items: center
  gap: 10px

  span
    color: #5f5f5f
    font-size: 13px
    transform: translateY(1px)

.hotel-card__early-late
  display: flex
  align-items: center
  gap: 30px
  margin-top: 5px

.hotel-card__early
  display: flex
  align-items: center
  gap: 6px
  color: #439ad1

.hotel-card__radios
  padding: 12px 30px 12px 12px
</style>
